import {enableProdMode} from '@angular/core';
import {platformBrowserDynamic} from '@angular/platform-browser-dynamic';
import * as Sentry from '@sentry/angular';
import {AppModule} from './app/app.module';
import {defaultAppConfig} from './app/tests/default-app-config';
import {environment} from './environments/environment';

if (environment.production) {
  enableProdMode();
}

const reloadRetriesLimit: number = 3;
const chunkLoadRetriesStorageKey: string = 'chunkLoadRetries';

if (typeof window !== 'undefined') {
  window.addEventListener('error', (event: any) => {
    if (
      event &&
      ((event.reason && event.reason.name === 'ChunkLoadError') ||
        (event.message && /Loading chunk [\d]+ failed/.test(event.message)))
    ) {
      Sentry.captureException(event.error || new Error(`ChunkLoadError: ${event.message || ''}`), {
        tags: {error_type: 'chunk_load_error', retries: sessionStorage.getItem(chunkLoadRetriesStorageKey) || '0'},
        level: 'error',
      });
      let retries: number = parseInt(sessionStorage.getItem(chunkLoadRetriesStorageKey), 10) || 0;
      setTimeout(() => {
        if (retries < reloadRetriesLimit) {
          retries++;
          sessionStorage.setItem(chunkLoadRetriesStorageKey, retries.toString());
          console.error('ChunkLoadError detected, forcing full page reload.');
          window.location.reload();
        } else {
          console.error('ChunkLoadError detected, failed to load chunk multiple times.');
        }
      }, 500);
    }
  });
}

function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
}

const environmentName = getCookie('environmentName');
const tracesSampleRate = parseFloat(getCookie('tracesSampleRate'));
const release = getCookie('release');

if (environmentName !== 'local') {
  const ignoredBrowsers = environmentName === 'production' ? ['HeadlessChrome', 'Coast', 'Applebot'] : [];
  const ignoredErrors =
    environmentName === 'production'
      ? [
          'pubads', // Doubleclick
          'dfp-targeting', // Google Ad manager
          'Error Code: 0', // Failed to get content from CF, blocked?
          'extension://', // Browser extension
          'CookieControl', // CookieBot
          'survey-v2', // Hotjar
          'BetterJsPop', // Ad blocker error
          'checkAndSaveView', // rek.ai
          'webkitExitFullScreen', // Webkit fullscreen
          'Error: AbortError: The operation was aborted.', // Video in mobile safari
          'NS_ERROR_FILE_CORRUPTED', // iOS error
          /.*zone\.js.*/,
          'PerformanceObserverInit.entryTypes is required', // Safari
        ]
      : [/.*zone\.js.*/];

  if (!ignoredBrowsers.some((browser) => navigator.userAgent.includes(browser))) {
    Sentry.onLoad(function () {
      Sentry.init({
        dsn: defaultAppConfig.sentryDsn,
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 1.0,
        tracePropagationTargets: ['localhost', 'https://www.varden.se/api'],
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration({
            maskAllText: false,
            maskAllInputs: false,
            blockAllMedia: false,
          }),
        ],
        debug: ['local', 'dev'].includes(environmentName),
        tracesSampleRate: tracesSampleRate,
        environment: environmentName,
        release: release,
        ignoreErrors: ignoredErrors,
      });
      Sentry.setUser({
        ip_address: '{{auto}}',
      });
    });
  }
}

void Sentry.startSpan(
  {
    name: 'platform-browser-dynamic',
    op: 'ui.angular.bootstrap',
  },
  async () => {
    await platformBrowserDynamic()
      .bootstrapModule(AppModule)
      .catch((err) => console.error(err));
  },
);
